// Putting files on a task, in one place.
//
// WHY THIS IS A MODULE AND NOT A SECOND COPY
//
// There are now two screens that attach a file: the panel on an existing task,
// and the New task dialog, which Luyanda asked for on 27 August 2026. The three
// steps below are not obvious, they are ordered for a reason, and the reason is
// written down in TaskAttachments.tsx. Two copies of a sequence like that stay
// identical for about a fortnight.
//
// The hook triggers are passed in rather than called here, because hooks cannot
// be called from a plain module and because that keeps this file testable without
// a React renderer.
//
// THE ORDER, AND WHY IT IS NOT THE OTHER ORDER
//
//   1. Ask the Worker for a one-shot signed URL. The Worker composes the storage
//      path, so a client can never name a key inside another task's folder.
//   2. PUT the bytes straight to Supabase Storage.
//   3. Tell the Worker the bytes landed, which writes the catalogue row.
//
// An upload that dies between 2 and 3 leaves an object nothing points at:
// invisible and harmless. Writing the row first would leave a file in the list
// that nobody can open, which somebody has to notice and report before anybody
// knows. See db/migrations/0084 and the project doc of 27 August 2026.
import { supabase } from '../../lib/supabase'
import { writeErrorMessage } from '../../utils/writeError'

/**
 * 25 MB, matching the bucket limit in db/migrations/0084.
 *
 * Deliberately far below the marketing bucket's 200 MB: a task attachment is a
 * screenshot, a quote, a claim form or an insurer's letter, and 200 MB is an
 * invitation to park a video on a task in a bucket nobody prunes.
 */
export const TASK_FILE_MAX_BYTES = 25 * 1024 * 1024

type Trigger = (params: Record<string, unknown>) => Promise<unknown>

export type UploadOutcome = {
  done: number
  /** One entry per file that failed, already worded for a person. */
  failed: string[]
}

/** The chosen files that are over the cap, so the caller can refuse them by name. */
export function oversizeFiles(files: File[]): File[] {
  return files.filter((f) => f.size > TASK_FILE_MAX_BYTES)
}

/**
 * Uploads each file and records it. Never throws.
 *
 * Counted rather than assumed, one file at a time. Several files can be chosen
 * at once and one of them can fail on its own; "3 of 4 added" is the only honest
 * report of that, and a single success toast would hide it.
 */
export async function uploadTaskFiles({
  taskId,
  files,
  sign,
  record,
}: {
  taskId: string
  files: File[]
  sign: Trigger
  record: Trigger
}): Promise<UploadOutcome> {
  let done = 0
  const failed: string[] = []

  for (const file of files) {
    try {
      const signed = (await sign({ task_id: taskId, filename: file.name })) as {
        path: string
        token: string
        bucket: string
      }
      if (!signed?.path || !signed?.token || !signed?.bucket) {
        throw new Error('No upload link came back for that file.')
      }

      const { error } = await supabase.storage
        .from(signed.bucket)
        .uploadToSignedUrl(signed.path, signed.token, file, {
          contentType: file.type || 'application/octet-stream',
        })
      if (error) throw new Error(error.message)

      await record({
        task_id: taskId,
        storage_path: signed.path,
        filename: file.name,
        mime_type: file.type || null,
        size_bytes: file.size,
      })
      done += 1
    } catch (err) {
      failed.push(`${file.name}: ${writeErrorMessage(err)}`)
    }
  }

  return { done, failed }
}
